import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import { GoldDrop } from "./goldDrop";
import { BasicAI } from "../ai/basicAI";
import { DamageType } from "../damageType";
import { Direction } from "../direction";
import { BallisticMovementEffect } from "../effects/ballisticMovementEffect";
import { BigExplosion } from "../effects/bigExplosion";
import { Timing } from "../effects/effect";
import { Hit } from "../effects/hit";
import { MovementEffect, STANDARD_MOVEMENT_TIME } from "../effects/movementEffect";
import { ParticleEffect } from "../effects/particleEffect";
import {
	PRIORITY_DEFAULT,
	type ControllableByAI,
	type LookAtHorizontalDirection,
	type ParticleEmitter,
	type ProcessableThing,
	type ProjectileHittable,
} from "../interfaces";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import type { PhysicalThing } from "../physicalThing";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import { STD_BASE_HP } from "../physicalThingWithHp";
import { SolidifierShot } from "../projectiles/solidifierShot";
import { PlasmaBall } from "../projectiles/plasmaBall";
import type { Thing } from "../thing";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";
import { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { ArtilleryAimIndicator, ArtilleryAimState } from "./artilleryAimIndicator";
import { ArtilleryBomb } from "./artilleryBomb";
import { MultiTileThingController } from "./multitile/multiTileThingController";
import { ParticleType } from "../particleType";
import { MultiTileThingCore } from "./multitile/multiTileThingCore";
import { MultiTileThingPart } from "./multitile/multiTileThingPart";
import { addDestroyEffectToBoard } from "./robotUtils";

export enum ArtilleryRobotStatus {
	NORMAL = "NORMAL",
	AIMING = "AIMING",
	RECHARGING = "RECHARGING",
}

const ARTILLERY_RANGE_MIN = 6;
const ARTILLERY_RANGE_MAX = 18;
const ARTILLERY_PRECISION = 0;
const ARTILLERY_SHOTS = 1;
const FIRING_DELAY = 3;
const RECHARGE_DELAY = 2;

export class ArtilleryRobot
	extends MultiTileThingCore
	implements ProcessableThing, ProjectileHittable, LookAtHorizontalDirection, ControllableByAI, ParticleEmitter
{
	static key = "ArtilleryRobot";

	override getBloodParticleType(): ParticleType {
		return ParticleType.ROBOT_DEBRIS;
	}

	getBaseGoldValue(): number {
		return getMobGoldValue(ArtilleryRobot.key);
	}

	getParticleType(): ParticleType | null {
		return this.hp < this.maxHp ? ParticleType.DAMAGE_SMOKE : null;
	}

	getParticleIntensity(): number {
		return 1 - (this.hp / this.maxHp);
	}

	private lookDirection: Direction = Direction.E;
	private ai: BasicAI;
	private status: ArtilleryRobotStatus = ArtilleryRobotStatus.RECHARGING;
	private aimingCountdown: number = 0;
	private rechargingCountDown: number = RECHARGE_DELAY;
	private aimIndicators: Set<ArtilleryAimIndicator> = new Set();
	private aimingSound: Sound | null = null;

	constructor() {
		super(true);
		this.hp = 100;
		this.maxHp = 100;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.ai = new BasicAI(this);
		this.ai.alwaysKnowsWhereTargetIs = true;
		this.ai.noiseCoolDown = 5;
		this.ai.noiseProbability = 0.5;
		this.ai.noiseSoundSupplier = () => new Sound(SoundId.ROBOT_SOUND, this.getXY(), 0.5);
	}

	static initOnBoard(xy: XY, board: SimpleBoard, _props?: Record<string, unknown>): PhysicalThing[] {
		const core = new ArtilleryRobot();
		core.xy = xy;

		const controller = new MultiTileThingController(core);
		core.controller = controller;
		board.putThing(core);

		const offsets = [new XY(0, 0), new XY(1, 0), new XY(0, 1), new XY(1, 1)];
		const parts: MultiTileThingPart[] = [];
		for (const offset of offsets) {
			const part = new MultiTileThingPart(xy.add(offset));
			part.linkToController(controller);
			controller.getParts().add(part);
			board.putThing(part);
			parts.push(part);
		}

		return [core, ...parts];
	}

	processTurn(_board: Board): void {
		if (this.status === ArtilleryRobotStatus.NORMAL) {
			this.ai.processTurn();
		} else if (this.status === ArtilleryRobotStatus.AIMING) {
			this.aimingCountdown--;
			if (this.aimingCountdown === 1) {
				for (const indicator of this.aimIndicators) {
					indicator.setState(ArtilleryAimState.STAGE2);
				}
				if (this.aimingSound) {
					this.board!.removeAtEndOfTurn(this.aimingSound);
				}
				this.aimingSound = new Sound(SoundId.ARTILLERY_AIMING_STAGE_2);
				this.aimingSound.loop = true;
				this.aimingSound.randomPitchVariation = 0;
				this.aimingSound.volume = 1;
				this.board!.addNonPhysicalThing(this.aimingSound);
			} else if (this.aimingCountdown <= 0) {
				this.fireArtilleryOnTargets();
				this.status = ArtilleryRobotStatus.RECHARGING;
				this.rechargingCountDown = RECHARGE_DELAY;
			}
		} else if (this.status === ArtilleryRobotStatus.RECHARGING) {
			this.rechargingCountDown--;
			if (this.rechargingCountDown <= 0) {
				this.status = ArtilleryRobotStatus.NORMAL;
			}
		}
	}

	getProcessPriority(): number {
		return PRIORITY_DEFAULT;
	}

	getLookingAtHorizontalDirection(): Direction {
		return this.lookDirection;
	}

	isPassable(): boolean {
		return false;
	}

	getZOrder(): number {
		return ZOrder.MOB;
	}

	isHideUnderneath(): boolean {
		return false;
	}

	onProjectileHit(projectile: unknown): void {
		if (projectile instanceof SolidifierShot) return;
		if (projectile instanceof PlasmaBall) return;
		this.board!.addNonPhysicalThing(new Sound(SoundId.METAL_BANG, this.xy));
	}

	// ControllableByAI implementation
	getBoard(): Board {
		return this.board!;
	}

	setLookDirection(direction: Direction): void {
		this.lookDirection = direction;
	}

	getXY(): XY {
		return this.controller ? this.controller.getBottomLeftXY() : this.xy;
	}

	moveTo(target: XY): void {
		if (!this.controller) return;
		const delta = target.subtract(this.getXY());
		for (const part of this.controller.getParts()) {
			const partTargetXY = part.xy.add(delta);
			this.board!.addEffect(new MovementEffect(part, part.xy, partTargetXY));
			this.board!.moveThing(part, partTargetXY);
		}
		// Only update xy — don't use board.moveThing because the core
		// is not in any cell (added via addNonPhysicalThing).
		this.xy = this.xy.add(delta);
	}

	canMoveTo(target: XY): boolean {
		if (!this.controller) return false;
		const delta = target.subtract(this.getXY());
		const coreTarget = this.xy.add(delta);
		if (!this.canUseSingleXY(coreTarget)) return false;
		for (const part of this.controller.getParts()) {
			const partTarget = part.xy.add(delta);
			if (!this.canUseSingleXY(partTarget)) return false;
		}
		return true;
	}

	canAttack(target: PhysicalThingWithHp): boolean {
		if (target == null) return false;
		const targetXY = target.xy;
		return this.isInMeleeRange(targetXY) || this.isInArtilleryRange(targetXY);
	}

	attack(target: PhysicalThingWithHp): void {
		if (this.isInMeleeRange(target.xy)) {
			this.meleeAttack(target);
		} else if (this.isInArtilleryRange(target.xy)) {
			this.startRangedAttack(target);
		}
	}

	protected onZeroHp(cause: Thing | null): void {
		if (this.board) {
			this.board.stats.recordMobKill(ArtilleryRobot.key);
			if (cause) this.board.stats.recordKillByWeapon((cause.constructor as any).key);
		}
		addDestroyEffectToBoard(this.board!, this);
		const goldAmount = this.getBaseGoldValue();
		if (goldAmount > 0 && this.board) {
			GoldDrop.spawnOrMerge(this.board, this.xy, goldAmount);
		}
		super.onZeroHp(cause);
	}

	override onRemoveFromBoard(board: Board): void {
		for (const indicator of this.aimIndicators) {
			board.removeThing(indicator);
		}
		this.aimIndicators.clear();
		if (this.aimingSound) {
			board.removeThing(this.aimingSound);
			this.aimingSound = null;
		}
	}

	private isInMeleeRange(target: XY): boolean {
		return this.getXY().isInSquareRadius(target, 1);
	}

	private isInArtilleryRange(target: XY): boolean {
		const dist = this.getXY().distanceTo(target);
		return dist >= ARTILLERY_RANGE_MIN && dist <= ARTILLERY_RANGE_MAX;
	}

	private meleeAttack(target: PhysicalThingWithHp): void {
		this.board!.addEffect(new Hit(target.xy));
		target.applyDamage(DamageType.MELEE, STD_BASE_HP, this);
	}

	private startRangedAttack(target: PhysicalThingWithHp): void {
		this.status = ArtilleryRobotStatus.AIMING;
		this.aimingCountdown = FIRING_DELAY;
		const targetPosition = target.xy;

		const deltaX = targetPosition.x - this.getXY().x;
		if (deltaX > 0) this.setLookDirection(Direction.E);
		if (deltaX < 0) this.setLookDirection(Direction.W);

		this.aimingSound = new Sound(SoundId.ARTILLERY_AIMING_STAGE_1);
		this.aimingSound.loop = true;
		this.aimingSound.randomPitchVariation = 0;
		this.aimingSound.volume = 1;
		this.board!.addNonPhysicalThing(this.aimingSound);

		const shotArea = targetPosition.getAreaInSquareRadius(ARTILLERY_PRECISION).filter((p) => p.isInside(this.board!));

		// Shuffle
		for (let i = shotArea.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[shotArea[i], shotArea[j]] = [shotArea[j], shotArea[i]];
		}

		let targetsPrepared = 0;
		for (const shotXY of shotArea) {
			this.createArtilleryTarget(shotXY);
			targetsPrepared++;
			if (targetsPrepared >= ARTILLERY_SHOTS) break;
		}
	}

	private createArtilleryTarget(targetPosition: XY): void {
		if (this.getXY().equals(targetPosition)) return;
		const indicator = new ArtilleryAimIndicator(targetPosition);
		this.board!.addThing(indicator);
		this.aimIndicators.add(indicator);
	}

	private fireArtilleryOnTargets(): void {
		for (const indicator of this.aimIndicators) {
			this.fireOnTarget(indicator.xy);
			this.board!.removeAtEndOfTurn(indicator);
		}
		this.aimIndicators.clear();
		if (this.aimingSound) {
			this.board!.removeAtEndOfTurn(this.aimingSound);
			this.aimingSound = null;
		}
	}

	private fireOnTarget(targetPosition: XY): void {
		const bomb = new ArtilleryBomb(this.getXY());
		const movementEffect = new BallisticMovementEffect(bomb, this.getXY(), targetPosition, STANDARD_MOVEMENT_TIME * 2);
		movementEffect.height = 2;
		this.board!.addEffect(movementEffect);
		const explosion = new BigExplosion(targetPosition);
		explosion.setAnimationOrder(2);
		this.board!.addEffect(explosion);
		this.board!.addEffect(new ParticleEffect(targetPosition, ParticleType.LARGE_GROUND_EXPLOSION, 30));
		this.damageArea(targetPosition);
		const explosionSound = new Sound(SoundId.EXPLOSION, targetPosition, 0.2);
		explosionSound.setTiming(Timing.BEFORE_STATE_UPDATE);
		explosionSound.setAnimationOrder(2);
		this.board!.addNonPhysicalThing(explosionSound);
	}

	private damageArea(center: XY): void {
		const blastArea = [center, ...center.getNeighbors()].filter((xy) => xy.isInside(this.board!));
		for (const blastXY of blastArea) {
			const things = this.board!.getThingsAt(blastXY).filter((t) => t.active && "applyDamage" in t);
			for (const t of things) {
				(t as PhysicalThingWithHp).applyDamage(DamageType.BLAST, STD_BASE_HP, this);
			}
		}
	}

	private canUseSingleXY(target: XY): boolean {
		if (!this.board!.isInBounds(target)) return false;
		if (this.xy.equals(target)) return true;
		if (this.controller) {
			for (const part of this.controller.getParts()) {
				if (part.xy.equals(target)) return true;
			}
		}
		if (this.board!.isPassable(target)) return true;
		return false;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			lookDirection: this.lookDirection,
			status: this.status,
			aimingCountdown: this.aimingCountdown,
			rechargingCountDown: this.rechargingCountDown,
		};
	}

	static deserialize(data: any): ArtilleryRobot {
		const a = new ArtilleryRobot();
		a.restoreCoreFields(data);
		a.lookDirection = data.lookDirection ?? Direction.E;
		a.status = data.status ?? ArtilleryRobotStatus.RECHARGING;
		a.aimingCountdown = data.aimingCountdown ?? 0;
		a.rechargingCountDown = data.rechargingCountDown ?? RECHARGE_DELAY;
		return a;
	}
}
